import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@v2/lib/utils";
import { LoadingSpinner } from "@cmp-src/loadings/LoadingSpinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-80 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground md:hover:bg-primary-hover",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        trigger:
          "flex border border-neutral-200 bg-background hover:bg-accent hover:text-accent-foreground max-w-full flex-row whitespace-nowrap rounded-[10px]",
        outlinePale:
          "border-1 border-neutral-100 text-secondary-text bg-background hover:border-neutral-50 disabled:text-neutral-200",
        secondary: "bg-neutral-300 text-secondary-text hover:bg-neutral-400",
        secondaryDashboard:
          "bg-neutral-100 text-secondary-text hover:bg-neutral-200",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        icon: "bg-transparent text-muted-foreground hover:text-primary",
        circleIcon:
          "bg-transparent text-muted-foreground opacity-90 hover:opacity-100 h-9 w-9 rounded-full p-2 flex items-center jusify-center transition-opacity"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        trigger: "max-h-[42px] h-[42px]"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={props.isLoading || props.disabled}
        children={
          props.isLoading ? (
            <div className={"relative"}>
              <div className={"opacity-0"}>{props.children}</div>
              <div className={"absolute inset-0"}>
                <LoadingSpinner />
              </div>
            </div>
          ) : (
            props.children
          )
        }
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
